// The viewer's time machine jumps (src/ui/timemachine.js): the next equinox
// or solstice, the next full or new Moon and the next eclipse after a given
// moment, from the same ephemeris the scene is drawn from. Pure, no DOM.
//
// Longitudes are of date: the planets' elements are J2000, so the Sun's and
// the Moon's are moved on by the general precession (1.397 degrees a
// century), as src/sky/events.js does for the Moon's phase. Left on J2000 the
// seasons came out about nine hours late in 2026.
import { getPlanetPosition } from '../orbits/ephemeris.js';
import { moonEcliptic } from '../orbits/moon.js';
import { upcomingSkyEvents } from './events.js';

const DEG = Math.PI / 180;
const J2000 = 2451545.0;
const MINUTE = 1 / 1440;

const wrap180 = (d) => ((((d + 180) % 360) + 360) % 360) - 180;
const wrap360 = (d) => ((d % 360) + 360) % 360;

// The Sun's geocentric ecliptic longitude of date, in degrees.
export function sunLongitudeOfDate(jd) {
  const e = getPlanetPosition('earth', jd);
  const T = (jd - J2000) / 36525;
  return wrap360(Math.atan2(-e.y, -e.x) / DEG + 1.3969713 * T);
}

// The Moon's elongation east of the Sun along the ecliptic, in degrees
// (-180..180): 0 at new Moon, 180 at full. moon.js is already of date.
export function moonElongation(jd) {
  return wrap180(moonEcliptic(jd).lon / DEG - sunLongitudeOfDate(jd));
}

// First time after jd that f (a wrapped angle, degrees) climbs through zero:
// steps, then bisection. The wrap from +180 to -180 is a fall, so it is never
// taken for a crossing.
function nextRise(f, jd, step, maxDays) {
  let a = jd;
  let fa = f(a);
  for (let t = jd + step; t <= jd + maxDays + step; t += step) {
    const fb = f(t);
    if (fa < 0 && fb >= 0 && fb - fa < 90) {
      let lo = a, hi = t;
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (f(mid) < 0) lo = mid; else hi = mid;
      }
      return (lo + hi) / 2;
    }
    a = t;
    fa = fb;
  }
  return null;
}

const SEASONS = ['March equinox', 'June solstice', 'September equinox', 'December solstice'];

// The next equinox or solstice strictly after jd: { jd, name }.
export function nextSeason(jd) {
  const from = jd + MINUTE;
  const k = (Math.floor(sunLongitudeOfDate(from) / 90) + 1) % 4;
  const at = nextRise((t) => wrap180(sunLongitudeOfDate(t) - k * 90), from, 1, 100);
  return at == null ? null : { jd: at, name: SEASONS[k] };
}

// The next new ('new') or full ('full') Moon strictly after jd: { jd, name }.
export function nextMoonPhase(jd, phase) {
  const target = phase === 'full' ? 180 : 0;
  const at = nextRise((t) => wrap180(moonElongation(t) - target), jd + MINUTE, 1, 32);
  return at == null ? null : { jd: at, name: phase === 'full' ? 'Full Moon' : 'New Moon' };
}

// The first moment strictly after jd when the Moon's elongation east of the
// Sun reaches deg (0 new, 90 first quarter, 180 full, 270 last quarter), as
// a JD, or null. Lessons use it to open a scene at a phase.
export function nextMoonElongation(jd, deg) {
  const target = wrap360(Number(deg) || 0);
  return nextRise((t) => wrap180(moonElongation(t) - target), jd + MINUTE, 1, 32);
}

// The next solar or lunar eclipse in src/sky/events.js's calendar after jd,
// or null past its end. The calendar starts in 2026 (CALENDAR_START), so an
// earlier date finds its first eclipse. Its events carry their greatest
// eclipse.
const CALENDAR_START = Date.UTC(2026, 0, 1);
export function nextEclipse(jd) {
  const ms = (jd - 2440587.5) * 86400000 + 60000;
  if (!Number.isFinite(ms)) return null;
  const from = new Date(Math.max(ms, CALENDAR_START));
  return upcomingSkyEvents({ from, days: 1200 }).find((e) => e.kind === 'eclipse-solar' || e.kind === 'eclipse-lunar') || null;
}
